import { Button } from '@bambi/component-library/src/components/Button/Button';
import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import {
  useOrganizationSettingsPartialUpdateMutation,
  useOrganizationSettingsRetrieveQuery,
} from 'src/common/external/bambi-api/bambiApi';
import { ConfirmModal } from 'src/common/modals/ConfirmModal';
import { Modal } from 'src/common/primitives/Modal';
import { show } from 'src/common/primitives/Toast/toast.slice';
import SecondsToHoursField from 'src/common/SecondsToHoursField';

const DEFAULT_LEAD_TIME_SECONDS = 86400;
const LEAD_TIME_WARN_THRESHOLD_SECONDS = 12 * 3600;

interface FormValues {
  public_trip_request_lead_time_seconds: number;
}

interface PublicTripRequestSettingsModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export function PublicTripRequestSettingsModal({
  open,
  setOpen,
}: PublicTripRequestSettingsModalProps) {
  return (
    <Modal
      open={open}
      setOpen={setOpen}
      onClose={() => setOpen(false)}
      title="Global Request Embed Settings"
    >
      <PublicTripRequestSettingsForm onClose={() => setOpen(false)} />
    </Modal>
  );
}

function PublicTripRequestSettingsForm({ onClose }: { onClose: () => void }) {
  const dispatch = useDispatch();
  const { data, isLoading } = useOrganizationSettingsRetrieveQuery({});
  const [save, { isLoading: isSaving }] =
    useOrganizationSettingsPartialUpdateMutation();
  const [pendingValues, setPendingValues] = useState<FormValues | null>(null);

  const methods = useForm<FormValues>({
    defaultValues: {
      public_trip_request_lead_time_seconds: DEFAULT_LEAD_TIME_SECONDS,
    },
  });

  useEffect(() => {
    if (data?.public_trip_request_lead_time_seconds != null) {
      methods.reset({
        public_trip_request_lead_time_seconds:
          data.public_trip_request_lead_time_seconds,
      });
    }
  }, [data, methods]);

  const persistSettings = async (values: FormValues) => {
    try {
      await save({
        patchedOrganizationSettings: {
          public_trip_request_lead_time_seconds:
            values.public_trip_request_lead_time_seconds,
        },
      }).unwrap();
      dispatch(
        show({
          title: 'Global request embed settings saved',
          type: 'success',
        })
      );
      onClose();
    } catch {
      dispatch(
        show({
          title: 'Failed to save global request embed settings',
          type: 'error',
        })
      );
    }
  };

  const onSubmit = async (values: FormValues) => {
    const isDirty =
      methods.formState.dirtyFields.public_trip_request_lead_time_seconds ===
      true;
    if (
      isDirty &&
      values.public_trip_request_lead_time_seconds <
        LEAD_TIME_WARN_THRESHOLD_SECONDS
    ) {
      setPendingValues(values);
      return;
    }
    await persistSettings(values);
  };

  if (isLoading) {
    return (
      <div className="flex min-w-[350px] items-center justify-center py-8">
        <LoadingIndicator />
      </div>
    );
  }

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(onSubmit)}
        className="flex min-w-[350px] flex-col gap-4"
      >
        <h2 className="text-lg font-medium">Global Request Embed Settings</h2>
        <p className="text-sm text-gray-500">
          Settings applied to every public trip request embed for this
          organization.
        </p>
        <SecondsToHoursField
          fieldPath="public_trip_request_lead_time_seconds"
          label="Lead time required before pickup, in hours"
          inlineHelperText="How far in advance trips must be booked through the public trip request form."
        />
        <div className="flex flex-row items-center justify-end gap-4">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </form>
      <ConfirmModal
        open={pendingValues !== null}
        setOpen={(isOpen) => {
          if (!isOpen) setPendingValues(null);
        }}
        mode="neutral"
        title="Short lead time"
        description="Are you sure you want to set the lead time to less than 12 hours? Someone will be able to request a ride at 8pm for 7am the next morning."
        cancelText="Cancel"
        confirmText="Yes, set short lead time"
        loading={isSaving}
        onCancel={() => setPendingValues(null)}
        onConfirm={async () => {
          if (!pendingValues) return;
          const values = pendingValues;
          setPendingValues(null);
          await persistSettings(values);
        }}
        testId="short-lead-time-confirm-modal"
      />
    </FormProvider>
  );
}
